import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useRoutes, Navigate, useLocation, useParams } from 'react-router-dom';

import { publicRoutes } from './public';

import { setOidcSignInState } from '@/app/api/baseApi';
import type { RootState } from '@/app/store';
import { FullscreenLoading } from '@/components/Elements/Loading/FullscreenLoading';
import { SorryPage } from '@/components/Elements/SorryPage/SorryPage';
import { REACT_APP_AUTH_INFO, REACT_APP_CUCON_API_DOMAIN } from '@/config';
import {
  AUTH_COOKIES,
  authenticationMethodType,
  CUCON_API_PATH,
} from '@/constants';
import { AmplifyAuthenticator } from '@/features/authenticator/AmplifyAuthenticator';
import {
  checkAndRefreshToken,
  getTenantId,
} from '@/features/authenticator/api/oidcAuth';
import {
  oidcSignIn,
  signIn,
  signOut,
} from '@/features/authenticator/authenticatorSlice';
import { GetToken } from '@/features/authenticator/components/GetToken';
import { fetchAuthorizationInfo } from '@/features/authorizationInfo/api/fetchAuthorizationInfo';
import {
  changeMaxZoom,
  changeMinZoom,
  changeZoom,
  changeBearing,
  changePitch,
} from '@/features/mapbox/mapSlice';
import { NotificationCancel } from '@/features/notification/components/NotificationCancel';
import { NotificationSetting } from '@/features/notification/components/NotificationSetting';
import { Preview } from '@/features/preview/Preview';
import { amplifyMemoryStorage, setAmplifyConfig } from '@/lib/amplify';
import { serviceProfile as jsonServiceProfile } from '@/profile/serviceProfile';
import { useServiceProfile } from '@/profile/util/useServiceProfile';
import '@aws-amplify/ui-react/styles.css';
import { CUCON_API_KEY_NAME, getToken } from '@/utils/storage';

let Home: React.FC;

const indexElement: string = jsonServiceProfile?.indexElement || '';

if (indexElement.length > 0) {
  Home = lazy(
    async () =>
      await import(`@/routes/${indexElement}`).catch(async (e) => {
        console.error('homeRouteがありません。:', e);

        Home = SorryPage;
      })
  );
} else if (jsonServiceProfile === undefined) {
  console.error('サポート対象外のサブドメインです');

  Home = SorryPage;
} else {
  console.error('homeRouteがありません。');

  Home = SorryPage;
}

const Loading = () => {
  const { navigationHeight } = useSelector((state: RootState) => state.drawer);

  return (
    <div
      data-testid="loading-overlay"
      className={
        ' visible fixed top-0 z-[101] w-full bg-black/60 opacity-100 duration-300 ease-in'
      }
      style={{ height: `calc(100% - ${navigationHeight}px)` }}
    >
      <FullscreenLoading />
    </div>
  );
};

export const AppRoutes = () => {
  const { isLoading } = useServiceProfile();
  const { serviceProfile } = useSelector(
    (state: RootState) => state.serviceProfile
  );
  const location = useLocation();
  const dispatch = useDispatch();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const { isSignIn, isOidcSignIn, isPreviewMode } = useSelector(
    (state: RootState) => state.authenticator
  );
  const { busStopId } = useParams();

  const [cookies, setCookie, removeCookie] = useCookies(AUTH_COOKIES);
  const processingRef = useRef(false); // 重複実行防止フラグ

  const authenticationMethod: string =
    jsonServiceProfile?.cuconConfig?.authenticationMethod ??
    authenticationMethodType.API_KEY;

  // 認証タイプがトークンの場合
  useEffect(() => {
    async () => {
      if (
        authenticationMethod === authenticationMethodType.TOKEN ||
        authenticationMethod === authenticationMethodType.ADMIN_TOKEN
      ) {
        const authorizationInfoApiUrl =
          jsonServiceProfile?.cuconConfig?.authorizationInfoUrl ??
          REACT_APP_CUCON_API_DOMAIN + CUCON_API_PATH.authorizationInfo;
        const authInfo =
          jsonServiceProfile?.cuconConfig?.authInfo ?? REACT_APP_AUTH_INFO;
        const tenantAlias = jsonServiceProfile?.cuconConfig?.tenantAlias;
        amplifyMemoryStorage.setTenantAlias(tenantAlias);
        try {
          const res = await fetchAuthorizationInfo(
            tenantAlias,
            authorizationInfoApiUrl,
            authInfo
          );
          if (res.data?.authorizationInfo[0] !== undefined) {
            setAmplifyConfig(res.data?.authorizationInfo[0]);
          }
        } catch (error) {
          console.error('getAuthorizationInfo: ', error);
        }
      }
    };
  }, [authenticationMethod]);

  // 認証タイプがAPI＆OIDCトークンの場合
  useEffect(() => {
    (async () => {
      if (processingRef.current) return; // 重複実行防止
      processingRef.current = true;
      if (
        authenticationMethod === authenticationMethodType.API_KEY_AND_TOKEN &&
        !isPreviewMode &&
        !isOidcSignIn
      ) {
        // coookieにaccessToken、refreshTokenが存在する場合は、トークン検証を行う
        if (
          cookies.accessToken !== undefined &&
          cookies.accessToken !== null &&
          cookies.refreshToken !== undefined &&
          cookies.refreshToken !== null
        ) {
          setIsSigningIn(true);
          const tokenAndUserInfo = await checkAndRefreshToken(
            cookies.accessToken,
            cookies.refreshToken
          );
          if (
            tokenAndUserInfo?.accessToken != null &&
            tokenAndUserInfo?.refreshToken != null
          ) {
            setCookie('accessToken', tokenAndUserInfo.accessToken, {
              path: '/',
            });
            setCookie('refreshToken', tokenAndUserInfo.refreshToken, {
              path: '/',
            });
            const apiKey =
              jsonServiceProfile?.cuconConfig?.apiKey ??
              getToken(CUCON_API_KEY_NAME);
            const tenantId = getTenantId(apiKey);
            setOidcSignInState(
              true,
              tokenAndUserInfo.accessToken ?? '',
              tokenAndUserInfo.refreshToken ?? '',
              tenantId
            );
            dispatch(
              signIn({
                id: tokenAndUserInfo.userId,
                username: tokenAndUserInfo.username,
                userGroupName: '',
                name: tokenAndUserInfo.name,
              })
            );
            dispatch(oidcSignIn());
            setIsSigningIn(false);
          } else {
            // Cookieのトークンが無効な場合は、サインアウト処理を行う
            AUTH_COOKIES.forEach((key) => removeCookie(key, { path: '/' }));
            dispatch(signOut());
            setIsSigningIn(false);
          }
        } else {
          dispatch(signOut());
        }
      }
    })();
  }, [
    authenticationMethod,
    cookies.accessToken,
    cookies.refreshToken,
    dispatch,
    isOidcSignIn,
    isPreviewMode,
    removeCookie,
    setCookie,
  ]);

  useEffect(() => {
    dispatch(changeMaxZoom(serviceProfile?.mapConfig?.maxZoom));
    dispatch(changeMinZoom(serviceProfile?.mapConfig?.minZoom));
    dispatch(changeZoom(serviceProfile?.mapConfig?.initialValue.zoom));
    dispatch(changeBearing(serviceProfile?.mapConfig?.initialValue.bearing));
    dispatch(changePitch(serviceProfile?.mapConfig?.initialValue.pitch));
  }, [serviceProfile, dispatch]);

  const routes = () => {
    // NOTE: serviceProfile読込中は、地図を表示しない
    if (isLoading || isSigningIn) {
      return [
        {
          path: '*',
          element: <Loading />,
        },
      ];
    }

    const authenticationMethod: string =
      jsonServiceProfile?.cuconConfig?.authenticationMethod ??
      authenticationMethodType.API_KEY;

    // NOTE: 認証タイプがトークンの場合は、サインイン状態になるまで地図を表示しない
    if (authenticationMethod === authenticationMethodType.TOKEN && !isSignIn) {
      return [
        {
          path: '*',
          element: <AmplifyAuthenticator />,
        },
      ];
    }

    return [
      {
        path: '/',
        element:
          // NOTE: 複数マップタイプがあれば、初期表示のマップタイプのurlにリダイレクト
          serviceProfile?.mapTypes.length >= 2 && busStopId !== undefined ? (
            <Navigate to={serviceProfile.mapTypes[0].id + location.search} />
          ) : (
            <Home />
          ),
        children: publicRoutes.children,
      },
      {
        path: ':mapType/:location',
        element: <Home />,
        children: publicRoutes.children,
      },
      {
        path: ':mapType',
        element: <Home />,
        children: publicRoutes.children,
      },
      {
        path: 'signin',
        element: <AmplifyAuthenticator />,
      },
      {
        path: 'get-token',
        element: <GetToken />,
      },
      {
        path: 'preview',
        element: <Preview />,
      },
      {
        path: 'notification/:servicesId',
        element: <NotificationSetting />,
      },
      {
        path: 'notification/cancel/:notificationUserId',
        element: <NotificationCancel />,
      },
    ];
  };

  const element = useRoutes(routes());

  return <Suspense fallback={<Loading />}>{element}</Suspense>;
};
